import React, { useState, useEffect } from "react";
import { Alert, Box, Button, Switch, Typography } from "@mui/material";
import api from "../../utils/api";
import { apiUrls } from "../../utils/urls";
import { useProfile } from "../../context/ProfileContext";
import usePushNotifications from "../../hooks/push/usePushNotifications";

const NotificationSettings = () => {
  const { profile, setProfile } = useProfile();
  const [allowTexts, setAllowTexts] = useState(profile.allow_texts);
  const [allowEmails, setAllowEmails] = useState(profile.allow_emails);
  const [pushStatusMessage, setPushStatusMessage] = useState("");
  const [isEnablingPush, setIsEnablingPush] = useState(false);
  const {
    supportsPushNotifications,
    notificationPermission,
    requestPermissionAndSubscribe,
  } = usePushNotifications({ enabled: true });

  useEffect(() => {
    setAllowTexts(profile.allow_texts);
    setAllowEmails(profile.allow_emails);
  }, [profile]);

  const handleToggleTexts = async () => {
    const newValue = !allowTexts;
    setAllowTexts(newValue);

    await api.post(apiUrls.users.profile(), {
      allow_texts: newValue,
    });
    setProfile((prev) => ({ ...prev, allow_texts: newValue }));
  };

  const handleToggleEmails = async () => {
    const newValue = !allowEmails;
    setAllowEmails(newValue);

    await api.post(apiUrls.users.profile(), {
      allow_emails: newValue,
    });
    setProfile((prev) => ({ ...prev, allow_emails: newValue }));
  };

  const handleEnablePushNotifications = async () => {
    setIsEnablingPush(true);
    setPushStatusMessage("");
    const result = await requestPermissionAndSubscribe();
    setIsEnablingPush(false);

    if (result.status === "subscribed" || result.status === "already_subscribed") {
      setPushStatusMessage(
        "Push notifications are enabled for this device/browser."
      );
      return;
    }
    if (result.status === "denied") {
      setPushStatusMessage(
        "Push permission is blocked. Enable notifications for this app in iPhone Settings > Notifications."
      );
      return;
    }
    if (result.status === "dismissed") {
      setPushStatusMessage(
        "Permission prompt was dismissed. Tap Enable Push Notifications again when ready."
      );
      return;
    }
    if (result.status === "unsupported") {
      setPushStatusMessage(
        "Push notifications are unavailable in this environment. Open the installed Home Screen app on a supported iOS version."
      );
      return;
    }

    setPushStatusMessage("Could not enable push notifications. Please try again.");
  };

  return (
    <Box>
      <Typography variant="h6">Notifications</Typography>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1">App Push Notifications</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Push notifications are pop-up alerts from this app on your phone. They
          are different from SMS text and email consent below.
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          Current permission: {notificationPermission}
        </Typography>
        <Button
          sx={{ mt: 1.5 }}
          variant="contained"
          onClick={handleEnablePushNotifications}
          disabled={isEnablingPush || !supportsPushNotifications}
        >
          {isEnablingPush ? "Enabling..." : "Enable Push Notifications"}
        </Button>
        {!supportsPushNotifications && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Push requires the installed PWA and a supported browser/device.
          </Typography>
        )}
        {pushStatusMessage && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {pushStatusMessage}
          </Alert>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Text and email switches control consent for SMS and email. Push
        notifications are separate and only apply to this device/browser.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
        <Typography>Allow Texts</Typography>
        <Switch
          checked={allowTexts}
          onChange={handleToggleTexts}
          color="secondary"
        />
      </Box>

      <Typography variant="body2" sx={{ mt: 1 }}>
        I consent to receive <strong>automated, transactional</strong> SMS
        messages from our services (e.g., check-in reminders, schedule updates).
        No promotional messages. Message &amp; data rates may apply. Reply{" "}
        <strong>STOP</strong> to stop at any time. Reply <strong>HELP</strong>{" "}
        for help. If you previously unsubscribed with STOP, reply{" "}
        <strong>START</strong> to resume.
      </Typography>

      <Typography
        variant="caption"
        display="block"
        mt={1}
        color="text.secondary"
      >
        You can revoke consent at any time by replying {'"'}STOP{'"'} or by
        unchecking
        {'"'}Allow texting{'"'} in your Notifications preferences. If messages
        were stopped and you’d like to resume, reply START to our number.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
        <Typography>Allow Emails</Typography>
        <Switch
          checked={allowEmails}
          onChange={handleToggleEmails}
          color="secondary"
        />
      </Box>
    </Box>
  );
};

export default NotificationSettings;
